import React, { Component } from 'react';
import Table, { ColumnProps } from 'antd/lib/table';
import umbrella from 'umbrella-storage';
import CompanyService, { CompanyListRequest, ICompanyInfo, ICompanyInfoList } from '../../service/company/CompanyService';
import './page-company.less';
import { getStates } from '../../components/public/StatesSelect';
import { Tooltip } from 'antd';

interface CompanyTableProps {
    nameInputValue: string;
    statesSelectValue: number | null;
    salesSelectValue: number | null;
    natureSelectValue: string | null;
    isMyCompanyPage: boolean; 
}

interface CompanyTableState {
    pageData: ICompanyInfoList | null;
    sourceData: ICompanyInfo[];
    nameInputValue: string;
    statesSelectValue: number | null;
    salesSelectValue: number | null;
    natureSelectValue: string | null;
    pagination: any;
    selectedRowKeys: number[];
}

//获取pageSize
const defaultPageSize = 5;
const getPageSize = () => {
    return umbrella.getLocalStorage('companyPageSize') ? umbrella.getLocalStorage('companyPageSize') : defaultPageSize;
}

const STORAGE_KEYS = {
    MY_COMPANY_PAGE: 'myCompanyCurrentPage',
    COMPANY_PAGE: 'companyCurrentPage'
};

//自定义CompanyTable
class CompanyTable extends Component<CompanyTableProps, CompanyTableState> {
    state: CompanyTableState = {
        pageData: {
            records: [],
            total: 0,
            size: 0,
            current: 1,
            pages: 0
        },
        sourceData: [],
        nameInputValue: '',
        statesSelectValue: null,
        salesSelectValue: null,
        natureSelectValue: null,
        pagination: {
            current: parseInt(localStorage.getItem('companyCurrentPage') || '1', 10),
            pageSize: getPageSize(),
            total: 0
        },
        selectedRowKeys: [],
    };

    componentDidUpdate(prevProps: Readonly<CompanyTableProps>, prevState: Readonly<CompanyTableState>) {
        if (prevState.pagination && prevState.pagination.pageSize !== this.state.pagination.pageSize) {
            this.getCompanyTableData(1);
        }
    }

    renderTooltip = (text) => {
        return (
          <Tooltip title={text}>
            <span>{text}</span>
          </Tooltip>
        );
    };

    //定义表格的列属性
    columns: ColumnProps<any>[] = [
        {
            title: '公司名',
            width: 80,
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: ICompanyInfo) => (
                <a href={`/#/app/customer/list?company=${record.id}`}>{text}</a>
            ),
        },
        { title: '销售', width: 25, dataIndex: 'salesName', key: 'salesName' },
        { title: '性质', width: 20, dataIndex: 'nature', key: 'nature'},
        { title: '总机', width: 50, dataIndex: 'phone', key: 'phone'},
        { title: '省份', width: 30, dataIndex: 'province', key: 'province'},
        { title: '地址', width: 130, dataIndex: 'address', key: 'address'},     
        { title: '行业', width: 50, dataIndex: 'industry', key: 'industry'},       
        { title: '状态', width: 30, dataIndex: 'states', key: 'states' },
        {
            title: '操作',
            key: 'operation',
            fixed: 'right',
            width: 72,
            className: 'company-operation-column',
            onHeaderCell: () => ({ style: { width: 72, minWidth: 72, maxWidth: 72 } }),
            onCell: () => ({ style: { width: 72, minWidth: 72, maxWidth: 72, whiteSpace: 'nowrap' } }),
            render: (record: ICompanyInfo) => (
                <div className="table-operation-buttons">
                    <button className="table-action-btn" onClick={() => this.handleEditBottonClick(record)}>编辑</button>
                </div>
            ),
        },
    ];
    
    // 允许外部组件手动重置分页
    setPaginationPage = (pageNo: number) => {
        this.setState({
            pagination: { ...this.state.pagination, current: pageNo }
        });
    };

    //获取指定页码的查询数据
    getCompanyTableData = async (pageNo: number, name?: string, states?: number, sales?: number, nature?: string) => {
        let response;
        const isMyCompanyPage = window.location.hash.includes('/app/my/company/list');
        
        if (isMyCompanyPage) {
            // 调用获取登录账户的公司列表接口
            response = await CompanyService.getCompanyByLoginAccount({
                companyQueryParam: {
                    name: name ?? this.state.nameInputValue ?? '',
                    states: states ?? this.state.statesSelectValue ?? undefined,
                    nature: nature ?? this.state.natureSelectValue ?? undefined,
                },
                pageNo,
                pageSize: getPageSize(),
            });
        } else {
            // 调用原有的获取公司列表接口
            const request: CompanyListRequest = {
                companyQueryParam: {
                    name: name ?? this.state.nameInputValue ?? '',
                    states: states ?? this.state.statesSelectValue ?? undefined,
                    sales: sales ?? this.state.salesSelectValue ?? undefined,
                    nature: nature ?? this.state.natureSelectValue ?? undefined,
                },
                pageNo,
                pageSize: getPageSize(),
            };
            response = await CompanyService.getCompanyListInfo(request);
        }

        // 处理返回数据的逻辑保持不变
        if (response) {
            const pageData: ICompanyInfoList = response.data && response.data.companyInfoList ? response.data.companyInfoList : [];
            const CompanyRecords = pageData && pageData.records ? response.data.companyInfoList.records : [];
            const mappedSourceData: ICompanyInfo[] = CompanyRecords.map((item: any) => ({
                id: item.id,
                salesName: item.salesName,
                name: item.name,
                nature: item.nature,
                phone: item.phone,
                province: item.province,
                address: item.address,
                industry: item.industry,
                states: getStates(item.states),
                introduction: item.introduction
            }));
            this.setState({
                pageData: { ...pageData, current: pageNo },
                sourceData: mappedSourceData,
                pagination: { ...this.state.pagination, total: pageData.total },
            });
        }
    };

    //页码切换事件
    handleChangePage = (pageNo: number) => {
        if (pageNo !== this.state.pagination.current) {
            // 根据页面类型选择对应的 storage key
            const storageKey = this.props.isMyCompanyPage 
                ? STORAGE_KEYS.MY_COMPANY_PAGE 
                : STORAGE_KEYS.COMPANY_PAGE;

            // 保存页码到对应的 storage key
            localStorage.setItem(storageKey, pageNo.toString());
            
            this.setState(
                (prevState) => ({
                    pagination: { ...prevState.pagination, current: pageNo },
                }),
                () => this.getCompanyTableData(
                    pageNo, 
                    this.props.nameInputValue, 
                    this.props.statesSelectValue, 
                    this.props.salesSelectValue,
                    this.props.natureSelectValue
                )
            );
        }
    };

    //PageSize选项卡事件
    changePageSize = (current: number, pageSize: number) => {
        umbrella.setLocalStorage('companyPageSize', pageSize);
        localStorage.setItem(STORAGE_KEYS.COMPANY_PAGE , "1");
        localStorage.setItem(STORAGE_KEYS.MY_COMPANY_PAGE , "1");
        this.setState({
            pagination: { current: 1, pageSize, total: this.state.pagination.total }
        });
    };


    //编辑按钮事件
    handleEditBottonClick = (companyData: ICompanyInfo) => {
        // 跳转公司管理页面
        const queryString = `?id=${companyData.id}`; 
        window.location.href = '/#/app/company/manager' + queryString;
    }

    // 添加选择改变的处理函数
    onSelectChange = (selectedRowKeys: number[]) => {
        this.setState({ selectedRowKeys });
    };

    render() {
        const { sourceData, pagination, selectedRowKeys } = this.state;
        const dataWithKeys = sourceData.map((row) => ({ ...row, key: row.id }));

        const rowSelection = {
            selectedRowKeys,
            onChange: this.onSelectChange,
            columnWidth: 10,
        };

        return (
            <div style={{ width: '100%', overflow: 'auto' }}>
                <Table size="small"
                    className="company-table"
                    rowSelection={rowSelection}
                    columns={this.columns}
                    dataSource={dataWithKeys}
                    tableLayout="fixed"
                    pagination={{
                        showSizeChanger: true,
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        pageSizeOptions: ['5', '10', '20', '50', '100'],
                        showTotal: (total, range) => `显示第 ${range[0]} 到第 ${range[1]} 条记录，共 ${total} 条记录`,
                        onShowSizeChange: this.changePageSize,
                        onChange: this.handleChangePage,
                    }}
                    scroll={{ x: 1580, y: 310 }}
                />
            </div>
        );
    }

};

export default CompanyTable;
